/* eslint-disable camelcase */
/* eslint-disable class-methods-use-this */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * author: Satyaki Bose
 * SyllabusIDBUtils.js
 * Utils for Syllabus IndexedDB storage
 */

const DB_NAME = 'AETSyllabusDB';
const DB_VERSION = 1;
const STORE_NAME = 'syllabus';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
}

export async function saveSyllabusToIndexedDB(key, data) {
  if (!key || !data) return;

  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.put({ key, data });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (e) {
    console.warn('Failed to save syllabus to IndexedDB:', e);
  }
}

export async function loadSyllabusFromIndexedDB(key) {
  if (!key) return null;

  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);

    const result = await new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });

    db.close();
    return result;
  } catch (e) {
    console.warn('Failed to load syllabus from IndexedDB:', e);
    return null;
  }
}

export async function clearSyllabusFromIndexedDB(key) {
  if (!key) return;

  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (e) {
    console.warn('Failed to clear syllabus from IndexedDB:', e);
  }
}

export async function hasSyllabusInIndexedDB(key) {
  if (!key) return false;
  try {
    const data = await loadSyllabusFromIndexedDB(key);
    if (data) {
      const hasSchedule = data?.syllabusInfo?.data?.schedule?.length > 0;
      const hasAssignments = data?.syllabusInfo?.assignments?.length > 0;
      return hasSchedule || hasAssignments;
    }
  } catch (e) {
    return false;
  }
  return false;
}

export async function clearAllSyllabusFromIndexedDB() {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (e) {
    console.warn('Failed to clear all syllabus from IndexedDB:', e);
  }
}
